import { useState, useEffect } from 'react';
import { createResource,getResources,deleteResource, updateResource} from '../../../router/api';
import toast from 'react-hot-toast';

interface BRDisponible {
  id: number;
  numero: string;
  date_reception: string;
  montant_restant: number;
}

export default function SupplierInvoiceForm({ invoice, onClose, onSave }: any) {
  const [fournisseurId, setFournisseurId] = useState('');
  const [dateFacture, setDateFacture] = useState(
    new Date().toISOString().split('T')[0]
  );
  const [modePaiement, setModePaiement] = useState('VIREMENT');
  const [notes, setNotes] = useState('');

  const [fournisseurs, setFournisseurs] = useState<any[]>([]);
  const [brDisponibles, setBrDisponibles] = useState<BRDisponible[]>([]);
  const [selectedBRs, setSelectedBRs] = useState<number[]>([]);
  const [paiementsBR, setPaiementsBR] = useState<Record<number, number>>({});
  const [loading, setLoading] = useState(false);

  /* =========================
   * Chargement fournisseurs
   * ========================= */
  useEffect(() => {
    getResources('fournisseurs')
      .then(r => setFournisseurs(r.data || []));
  }, []);

  /* =========================
   * PRÉ-REMPLISSAGE ÉDITION (CORRIGÉ)
   * ========================= */
  useEffect(() => {
    if (!invoice) return;

    // ✅ fournisseur
    setFournisseurId(String(invoice.fournisseur?.id || ''));

    // ✅ date
    setDateFacture(invoice.date_facture);

    // ✅ notes
    setNotes(invoice.notes || '');

    // ✅ mode paiement
    setModePaiement(invoice.mode_paiement || 'VIREMENT');

    // ✅ BR sélectionnés
    const brIds =
      invoice.bonsReception?.map((br: any) => br.id) || [];
    setSelectedBRs(brIds);

    // ✅ montants payés par BR
    const paiements: Record<number, number> = {};
    invoice.bonsReception?.forEach((br: any) => {
      if (br.montant_paye > 0) {
        paiements[br.id] = br.montant_paye;
      }
    });
    setPaiementsBR(paiements);
  }, [invoice]);

  /* =========================
   * Chargement BR du fournisseur
   * ========================= */
  useEffect(() => {
    if (!fournisseurId) {
      setBrDisponibles([]);
      if (!invoice) {
        setSelectedBRs([]);
        setPaiementsBR({});
      }
      return;
    }

    getResources('bons-reception/disponibles', {
      params: { fournisseur_id: fournisseurId }
    })
      .then(r => {
        setBrDisponibles(r.data.data || []);
        if (!invoice) {
          setSelectedBRs([]);
          setPaiementsBR({});
        }
      });
  }, [fournisseurId]);

  /* =========================
   * Sélection BR
   * ========================= */
  const toggleBR = (id: number) => {
    setSelectedBRs(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );

    setPaiementsBR(prev => {
      const copy = { ...prev };
      delete copy[id];
      return copy;
    });
  };

  const handlePaiementChange = (brId: number, value: string) => {
    const montant = parseFloat(value) || 0;
    const br = brDisponibles.find(b => b.id === brId);
    if (!br || montant > br.montant_restant) return;

    setPaiementsBR(prev => ({
      ...prev,
      [brId]: montant
    }));
  };

  /* =========================
   * Calculs
   * ========================= */
  const brSelectionnes = brDisponibles.filter(br =>
    selectedBRs.includes(br.id)
  );

  const totalHT = brSelectionnes.reduce(
    (sum, br) => sum + br.montant_restant,
    0
  );
  const tva = Math.round(totalHT * 0.1925);
  const totalTTC = totalHT + tva;

  const montantPayeTotal = Object.values(paiementsBR).reduce(
    (a, b) => a + b,
    0
  );

  const hasPaiement = montantPayeTotal > 0;

  const statut =
    montantPayeTotal >= totalTTC
      ? 'Soldée'
      : montantPayeTotal > 0
      ? 'Payée partiellement'
      : 'Impayée';

  /* =========================
   * Submit
   * ========================= */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (selectedBRs.length === 0) {
      toast.error('Veuillez sélectionner au moins un bon de réception');
      return;
    }

    const paiements_br = Object.entries(paiementsBR)
      .filter(([, montant]) => montant > 0)
      .map(([id, montant]) => ({
        br_id: Number(id),
        montant_paye: montant
      }));

    const payload = {
      fournisseur_id: Number(fournisseurId),
      br_ids: selectedBRs,
      date_facture: dateFacture,
      mode_paiement: hasPaiement
        ? modePaiement
        : invoice?.mode_paiement ?? null,
      paiements_br,
      notes: notes || null
    };

    try {
      setLoading(true);

      const res = invoice
        ? await updateResource(
            `factures-fournisseur`,
            invoice.id,
            payload
          )
        : await createResource(
            'factures-fournisseur',
            payload
          );

      toast.success(
        invoice
          ? 'Facture modifiée avec succès'
          : 'Facture créée avec succès'
      );

      onSave(res.data.data);
      onClose();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Erreur');
    } finally {
      setLoading(false);
    }
  };

  /* =========================
   * RENDER (Design inchangé)
   * ========================= */
  return (
    <div className="bg-white rounded-lg shadow-sm border">
      <div className="p-6 border-b flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button onClick={onClose} className="w-10 h-10 bg-gray-100 rounded-lg hover:bg-gray-200">
            <i className="ri-arrow-left-line text-xl"></i>
          </button>
          <div>
            <h2 className="text-2xl font-bold">
              {invoice ? 'Modifier la facture fournisseur' : 'Nouvelle Facture Fournisseur'}
            </h2>
            <p className="text-sm text-gray-600">Sélection + règlement partiel possible</p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="p-6 space-y-8">

        {/* Fournisseur + Date */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block font-medium mb-2">Fournisseur *</label>
            <select value={fournisseurId} onChange={e => setFournisseurId(e.target.value)} required className="w-full px-4 py-2 border rounded-lg">
              <option value="">Choisir un fournisseur</option>
              {fournisseurs.map(f => (
                <option key={f.id} value={f.id}>{f.raisonSociale}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block font-medium mb-2">Date facture *</label>
            <input type="date" value={dateFacture} onChange={e => setDateFacture(e.target.value)} required className="w-full px-4 py-2 border rounded-lg" />
          </div>
        </div>

        {/* Sélection des BR */}
        {brDisponibles.length > 0 && (
          <div className="border rounded-lg p-5 bg-gray-50">
            <h3 className="font-semibold text-lg mb-4">Sélectionner les bons de réception à facturer</h3>
            <div className="space-y-3">
              {brDisponibles.map(br => (
                <label
                  key={br.id}
                  className={`flex items-center justify-between p-4 rounded-lg border-2 cursor-pointer transition-all ${
                    selectedBRs.includes(br.id)
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center gap-4">
                    <input
                      type="checkbox"
                      checked={selectedBRs.includes(br.id)}
                      onChange={() => toggleBR(br.id)}
                      className="w-5 h-5 text-blue-600 rounded"
                    />
                    <div>
                      <div className="font-medium">{br.numero}</div>
                      <div className="text-sm text-gray-600">
                        {new Date(br.date_reception).toLocaleDateString('fr-FR')} • Restant : {br.montant_restant.toLocaleString()} GNF
                      </div>
                    </div>
                  </div>
                </label>
              ))}
            </div>
          </div>
        )}

        {/* Répartition du règlement (facultatif) */}
        {selectedBRs.length > 0 && (
          <div className="bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded-xl p-6">
            <h3 className="text-xl font-bold text-emerald-900 mb-4">
              Répartition du règlement immédiat (facultatif)
            </h3>
            <p className="text-sm text-emerald-800 mb-5">
              Saisissez un montant pour payer immédiatement une partie du bon. Laissez vide = pas de paiement.
            </p>

            <div className="space-y-4">
              {brSelectionnes.map(br => {
                const paye = paiementsBR[br.id] || 0;
                const reste = br.montant_restant - paye;

                return (
                  <div key={br.id} className="bg-white p-4 rounded-lg border">
                    <div className="flex justify-between items-center mb-3">
                      <span className="font-semibold">{br.numero}</span>
                      <span className="text-sm text-gray-600">
                        À facturer : <strong>{br.montant_restant.toLocaleString()} GNF</strong>
                      </span>
                    </div>
                    <input
                      type="number"
                      placeholder="Montant payé maintenant (facultatif)"
                      value={paye || ''}
                      onChange={e => handlePaiementChange(br.id, e.target.value)}
                      className="w-full px-4 py-3 border rounded-lg text-right font-medium"
                      min="0"
                      max={br.montant_restant}
                      step="1000"
                    />
                    {paye > 0 && (
                      <div className="mt-2 text-right text-sm">
                        Reste après paiement :{' '}
                        <strong className={reste <= 0 ? 'text-green-600' : 'text-orange-600'}>
                          {reste.toLocaleString()} GNF
                        </strong>
                        {reste <= 0 && (
                          <span className="ml-2 px-3 py-1 bg-green-100 text-green-800 rounded-full text-xs font-bold">
                            Soldé
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Mode de paiement (visible seulement si paiement) */}
            {hasPaiement && (
              <div className="mt-6 flex items-center justify-end gap-4">
                <label className="font-medium text-emerald-900">Mode de paiement</label>
                <select
                  value={modePaiement}
                  onChange={e => setModePaiement(e.target.value)}
                  className="px-5 py-3 border-2 border-emerald-300 rounded-lg font-medium"
                >
                  <option value="VIREMENT">Virement</option>
                  <option value="CHEQUE">Chèque</option>
                  <option value="ESPECES">Espèces</option>
                </select>
              </div>
            )}

            <div className="mt-6 pt-5 border-t-2 border-emerald-200 text-right">
              <span className="text-xl font-bold text-emerald-900">
                Total payé immédiatement : {montantPayeTotal.toLocaleString()} GNF
              </span>
            </div>
          </div>
        )}

        {/* Résumé */}
        {selectedBRs.length > 0 && (
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-8 rounded-xl">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-3xl font-bold">Résumé de la facture</h3>
              <span className="px-6 py-3 bg-white text-indigo-700 rounded-full font-bold text-lg">
                {statut}
              </span>
            </div>
            <div className="grid grid-cols-3 gap-8 text-center">
              <div>
                <p className="text-blue-200">Total HT</p>
                <p className="text-4xl font-bold">{totalHT.toLocaleString()} GNF</p>
              </div>
              <div>
                <p className="text-blue-200">TVA 19,25%</p>
                <p className="text-4xl font-bold">{tva.toLocaleString()} GNF</p>
              </div>
              <div>
                <p className="text-blue-200">Total TTC</p>
                <p className="text-5xl font-bold">{totalTTC.toLocaleString()} GNF</p>
              </div>
            </div>
            {hasPaiement && (
              <div className="text-center mt-6">
                <p className="text-blue-200">Payé immédiatement</p>
                <p className="text-4xl font-bold text-green-300">{montantPayeTotal.toLocaleString()} GNF</p>
              </div>
            )}
          </div>
        )}

        {/* Notes */}
        <div>
          <label className="block font-medium mb-2">Notes internes</label>
          <textarea
            value={notes}
            onChange={e => setNotes(e.target.value)}
            rows={3}
            className="w-full px-4 py-2 border rounded-lg"
            placeholder="Ex: facture papier reçue..."
          />
        </div>

        {/* Boutons */}
        <div className="flex justify-end gap-4 pt-6 border-t">
          <button type="button" onClick={onClose} className="px-6 py-3 border rounded-lg">
            Annuler
          </button>
          <button
            type="submit"
            disabled={loading || selectedBRs.length === 0}
            className="px-8 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2 font-medium"
          >
            {loading && <i className="ri-loader-4-line animate-spin"></i>}
            Créer la facture
          </button>
        </div>
      </form>
    </div>
  );
}